import React from "react";
import { Link } from "react-scroll";
import { motion, useScroll, useTransform } from "framer-motion";
import Lottie from "lottie-react";
import { BsArrowUpRight, BsGithub, BsLinkedin } from "react-icons/bs";
import { HiOutlineMail } from "react-icons/hi";
import liveChatbotAnimation from "../assets/Live chatbot.json";

const quickFacts = [
  { label: "Based in", value: "Bengaluru, India" },
  { label: "Experience", value: "2+ years across commerce products" },
  { label: "Current role", value: "Full Stack Developer, Easybiznus" },
];

const impactStats = [
  { value: "2+ yrs", label: "Engineering payments and order systems" },
  { value: "~50/day", label: "Multi-vendor orders routed and split" },
  { value: "99%", label: "Order-event reliability after redesign" },
  { value: "10+", label: "Vendors on shared-inventory fulfillment" },
];

const heroHighlights = [
  {
    label: "What I work on",
    value: "The backbone of commerce apps — checkout, multi-vendor orders, and the reliability layer underneath",
  },
  {
    label: "Core stack",
    value: "React, Next.js, React Native, Node.js, Express, PostgreSQL, TypeScript, AWS",
  },
  {
    label: "How I build",
    value: "Idempotency, concurrency and locking, retry/backoff, async processing, CI/CD",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const Home = () => {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 500], [0, 60]);

  return (
    <section
      name="home"
      className="relative overflow-hidden px-4 pb-10 pt-28 sm:px-6 lg:px-8"
    >
      <div className="absolute inset-x-0 top-14 h-60 bg-[radial-gradient(circle,_rgba(245,158,11,0.18),_transparent_68%)] blur-3xl" />

      <div className="mx-auto grid min-h-[calc(100svh-7rem)] max-w-7xl items-center gap-16 lg:grid-cols-[1.12fr_0.88fr]">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.14,
              },
            },
          }}
          className="relative z-10"
        >
          <motion.div
            variants={fadeUp}
            className="eyebrow-pill inline-flex items-center gap-3 rounded-full px-4 py-2 text-sm text-slate-200"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#f59e0b]" />
            Full Stack Developer
            <span className="text-slate-500">|</span>
            Commerce &amp; Payments Infrastructure
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="section-title mt-6 max-w-4xl"
          >
            I build the
            <span className="bg-gradient-to-r from-[#f59e0b] via-[#f8fafc] to-[#67e8f9] bg-clip-text text-transparent">
              {" "}payment and order systems{" "}
            </span>
            that keep real commerce running.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="section-copy mt-8 text-slate-300"
          >
            For over two years I&apos;ve worked on the backbone of commerce
            apps — Razorpay checkout flows, idempotent Shopify webhook pipelines,
            multi-vendor order routing, and a custom job queue behind core B2B and
            B2C operations. I moved from intern to Full Stack Developer in a month
            by shipping work that holds up under real traffic.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link
              to="projects"
              smooth
              duration={500}
              offset={-80}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#f59e0b] px-6 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-[#fbbf24]"
            >
              View Projects
              <BsArrowUpRight />
            </Link>

            <a
              href="/resume.html"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/14 px-6 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-slate-100 transition hover:border-cyan-300/60 hover:text-white"
            >
              Download Resume
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-wrap items-center gap-3 text-slate-300"
          >
            <a
              href="https://www.linkedin.com/in/iamsinghshubhamm/"
              target="_blank"
              rel="noreferrer"
              className="eyebrow-pill inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition hover:border-cyan-300/50 hover:text-white"
            >
              <BsLinkedin />
              LinkedIn
            </a>
            <a
              href="https://github.com/iamsinghshubhamm"
              target="_blank"
              rel="noreferrer"
              className="eyebrow-pill inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition hover:border-cyan-300/50 hover:text-white"
            >
              <BsGithub />
              GitHub
            </a>
            <a
              href="mailto:iamsinghshubhamm@gmail.com"
              className="eyebrow-pill inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition hover:border-cyan-300/50 hover:text-white"
            >
              <HiOutlineMail />
              Email
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-14 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3"
          >
            {quickFacts.map((fact) => (
              <div key={fact.label}>
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                  {fact.label}
                </p>
                <p className="mt-3 max-w-[14rem] text-sm font-medium text-slate-100 sm:text-base">
                  {fact.value}
                </p>
              </div>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/5 sm:grid-cols-4"
          >
            {impactStats.map((stat) => (
              <div
                key={stat.label}
                className="group relative bg-[#0b1220] p-5 transition-colors duration-300 hover:bg-[#0e1626]"
              >
                <span className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-[#f59e0b]/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <p className="font-['Sora'] text-2xl text-[#f59e0b] sm:text-3xl">
                  {stat.value}
                </p>
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: imageY }}
          initial={{ opacity: 0, scale: 0.92, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="relative mx-auto flex w-full max-w-[440px] justify-center lg:justify-end"
        >
          <div className="hero-ring" aria-hidden="true" />
          <div className="relative w-full overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b1220] p-6 sm:p-8">
            <div className="absolute -left-14 top-8 h-40 w-40 rounded-full bg-cyan-300/14 blur-3xl" />
            <div className="absolute -right-12 bottom-8 h-40 w-40 rounded-full bg-amber-400/12 blur-3xl" />

            <div className="surface-outline relative overflow-hidden rounded-[1.5rem] p-4 sm:p-5">
              <div className="absolute inset-x-8 top-0 h-20 rounded-full bg-cyan-300/10 blur-3xl" />
              <p className="relative z-10 text-sm tracking-[0.28em] text-cyan-300">
                Reliable systems for real-world commerce.
              </p>
              <div className="relative z-10 mt-2">
                <Lottie
                  animationData={liveChatbotAnimation}
                  loop
                  autoplay
                  className="mx-auto h-[280px] w-full max-w-[340px] sm:h-[320px]"
                />
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              {heroHighlights.map((item) => (
                <div
                  key={item.label}
                  className="rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-5"
                >
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-500">
                    {item.label}
                  </p>
                  <p className="mt-3 text-sm leading-7 text-slate-200 sm:text-base">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Home;
